import { useEffect, useState } from 'react';

import { useVersion } from '@/lib/debug/debug';

/**
 * Map of environment-specific favicon imports.
 * Each environment (prod, development, stage) has two variants:
 * - default: Regular favicon for the environment
 * - preview: Preview-specific favicon for the environment
 */
const faviconMap = {
  prod: {
    default: () => import('@/assets/favicons/favicon-prod.svg'),
    preview: () => import('@/assets/favicons/favicon-prod-preview.svg'),
  },
  development: {
    default: () => import('@/assets/favicons/favicon-development.svg'),
    preview: () => import('@/assets/favicons/favicon-development-preview.svg'),
  },
  stage: {
    default: () => import('@/assets/favicons/favicon-stage.svg'),
    preview: () => import('@/assets/favicons/favicon-stage-preview.svg'),
  },
} as const;

/**
 * Favicon component that dynamically updates the website's favicon based on the current environment
 * and preview status.
 *
 * This component:
 * 1. Uses the current environment and preview status from useVersion hook
 * 2. Dynamically imports the appropriate favicon SVG based on environment and preview status
 * 3. Updates the document's favicon link element when the favicon URL changes
 *
 * Note: In production (non-preview) environment, this component does nothing as the favicon
 * is already set via index.html
 *
 * @returns {null} This component doesn't render anything in the DOM
 *
 * @example
 * // The component will automatically update the favicon based on the environment
 * <Favicon />
 */
export const Favicon = () => {
  const { environment, isPreview } = useVersion();
  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);

  useEffect(() => {
    // Skip favicon handling for production non-preview environment
    if (environment === 'prod' && !isPreview) {
      return;
    }

    const loadFavicon = async () => {
      try {
        const importFn =
          faviconMap[environment as keyof typeof faviconMap]?.[
            isPreview ? 'preview' : 'default'
          ];
        if (importFn) {
          const module = await importFn();
          setFaviconUrl(module.default);
        }
      } catch (error) {
        console.error('Failed to load favicon:', error);
      }
    };

    loadFavicon();
  }, [environment, isPreview]);

  useEffect(() => {
    // Skip favicon setting for production non-preview environment
    if (environment === 'prod' && !isPreview) {
      return;
    }

    if (faviconUrl) {
      const favicon = document.querySelector("link[rel='icon']");
      if (favicon) {
        favicon.setAttribute('href', faviconUrl);
      }
    }
  }, [faviconUrl, environment, isPreview]);

  return null;
};
